import React from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';

import repairRatesQuery from './repairRatesQuery.graphql';
import saveRepairRateMutation from './saveRepairRateMutation.graphql';

// What operations charges for a repair.
//
// The ticket's criterion is that pricing is editable without a deploy, so this
// screen is the feature — not a convenience on top of it. A price list that
// only a developer can change is wrong within a year and stays wrong until
// someone schedules a release.
//
// Nothing is seeded. An empty table produces "incomplete" estimates, which is
// the honest answer until real prices exist, and an incomplete estimate is
// visible in a way a wrong price is not.
//
// Styled inline, like the panels beside it: package.json pins bootstrap 3.4.1
// while this screen uses Bootstrap 5 utility classes, so a component depending
// on those may render unstyled.

const styles = {
  panel: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '20px', marginBottom: '24px', backgroundColor: '#fbfbfb' },
  heading: { fontWeight: 'bold', fontSize: '18px', marginBottom: '4px' },
  sub: { color: '#666', marginBottom: '16px', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', borderBottom: '2px solid #e0e0e0', padding: '8px 6px', color: '#666', fontWeight: 'normal' },
  td: { borderBottom: '1px solid #eee', padding: '8px 6px' },
  input: { width: '100%', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' },
  narrow: { width: '110px' },
  button: { padding: '6px 14px', border: '1px solid #0d6f8f', borderRadius: '4px', backgroundColor: '#0d6f8f', color: '#fff', cursor: 'pointer', fontSize: '14px' },
  secondary: { padding: '6px 14px', border: '1px solid #999', borderRadius: '4px', backgroundColor: '#fff', color: '#333', cursor: 'pointer', fontSize: '14px', marginLeft: '8px' },
  error: { color: '#b00020', fontSize: '13px', marginTop: '10px' },
  ok: { color: '#2d8f4b', fontSize: '13px', marginTop: '10px' },
  empty: { backgroundColor: '#fff8e5', border: '1px solid #f0d9a0', borderRadius: '4px', padding: '12px', fontSize: '14px', marginBottom: '16px' },
  scroll: { overflowX: 'auto' },
  retired: { color: '#999' },
};

const SEVERITIES = ['Minor', 'Moderate', 'Severe'];

const BLANK = { id: null, partName: '', severity: 'Minor', amount: '', currency: 'USD', notes: '' };

class RepairRatesPanel extends React.Component {
  static propTypes = {
    rates: PropTypes.object,
    saveRate: PropTypes.func.isRequired,
  };

  state = { form: { ...BLANK }, error: null, saved: false, busy: false };

  setField = (name, value) => {
    this.setState((s) => ({ form: { ...s.form, [name]: value }, error: null, saved: false }));
  };

  edit = (rate) => {
    this.setState({
      form: {
        id: rate.id,
        partName: rate.partName || '',
        severity: rate.severity,
        // Rendered as text so an empty box stays empty. Putting 0 here would
        // be a price nobody chose, and the whole point of this screen is that
        // every number on it was chosen.
        amount: rate.amount === null || rate.amount === undefined ? '' : String(rate.amount),
        currency: rate.currency || 'USD',
        notes: rate.notes || '',
      },
      error: null,
      saved: false,
    });
  };

  cancel = () => this.setState({ form: { ...BLANK }, error: null, saved: false });

  save = async (overrides = {}) => {
    const form = { ...this.state.form, ...overrides };

    // Checked here as well as in the API, because a blank box reaching the
    // server as 0 is a repair nobody gets charged for, and nothing downstream
    // can tell that from a deliberate discount.
    const raw = String(form.amount).trim();
    if (raw === '') {
      this.setState({ error: 'Enter an amount. A blank amount is not a price of zero.' });
      return;
    }

    const amount = Number(raw);
    if (!isFinite(amount) || amount < 0) {
      this.setState({ error: 'The amount must be a number and cannot be negative.' });
      return;
    }

    this.setState({ busy: true, error: null, saved: false });

    try {
      const { data } = await this.props.saveRate({
        variables: {
          id: form.id || undefined,
          partName: String(form.partName || '').trim() || null,
          severity: form.severity,
          amount,
          currency: String(form.currency || 'USD').trim().toUpperCase(),
          active: form.active === undefined ? true : form.active,
          notes: String(form.notes || '').trim() || null,
        },
        refetchQueries: [{ query: repairRatesQuery, variables: { includeInactive: true } }],
      });

      const result = data && data.saveRepairRate;
      if (!result || result.status !== 200) {
        this.setState({ busy: false, error: (result && result.errorMessage) || 'Could not save the rate.' });
        return;
      }

      this.setState({ busy: false, form: { ...BLANK }, saved: true });
    } catch (e) {
      this.setState({ busy: false, error: e && e.message ? e.message : 'Could not save the rate.' });
    }
  };

  // Retiring rather than deleting: a stored estimate names the rate it used,
  // and removing the row destroys the reason a past figure was that figure.
  toggleActive = (rate) => {
    this.save({
      id: rate.id,
      partName: rate.partName || '',
      severity: rate.severity,
      amount: rate.amount === null || rate.amount === undefined ? '' : String(rate.amount),
      currency: rate.currency,
      notes: rate.notes || '',
      active: !rate.active,
    });
  };

  render() {
    const { rates } = this.props;
    const { form, error, saved, busy } = this.state;

    if (!rates || rates.loading) return null;

    const result = rates.getRepairRates;
    if (!result || result.status !== 200) return null;

    const list = result.results || [];

    return (
      <div style={styles.panel}>
        <div style={styles.heading}>Repair rates</div>
        <div style={styles.sub}>
          What a repair costs, by severity. A rate naming a part overrides the general rate for
          that severity, so a severe windscreen can be priced apart from a severe wing mirror.
        </div>

        {list.length === 0 && (
          <div style={styles.empty}>
            No rates are set, so every estimate reads as <strong>incomplete</strong> and nothing can
            be charged. That is deliberate — no starting prices were invented, because a guess in
            this table would look exactly like a decision someone made.
          </div>
        )}

        <div style={styles.scroll}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Part</th>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Currency</th>
                <th style={styles.th}>Note</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {list.map((rate) => (
                <tr key={rate.id} style={rate.active ? undefined : styles.retired}>
                  <td style={styles.td}>{rate.partName || <em>any part</em>}</td>
                  <td style={styles.td}>{rate.severity}</td>
                  <td style={styles.td}>{rate.amount}</td>
                  <td style={styles.td}>{rate.currency}</td>
                  <td style={styles.td}>{rate.notes}{rate.active ? '' : ' (retired)'}</td>
                  <td style={styles.td}>
                    <button type="button" style={styles.secondary} onClick={() => this.edit(rate)}>Edit</button>
                    <button type="button" style={styles.secondary} onClick={() => this.toggleActive(rate)}>
                      {rate.active ? 'Retire' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}

              <tr>
                <td style={styles.td}>
                  <input
                    style={styles.input}
                    placeholder="any part"
                    value={form.partName}
                    onChange={(e) => this.setField('partName', e.target.value)}
                  />
                </td>
                <td style={styles.td}>
                  <select
                    style={styles.input}
                    value={form.severity}
                    onChange={(e) => this.setField('severity', e.target.value)}
                  >
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td style={styles.td}>
                  <input
                    style={{ ...styles.input, ...styles.narrow }}
                    value={form.amount}
                    onChange={(e) => this.setField('amount', e.target.value)}
                  />
                </td>
                <td style={styles.td}>
                  <input
                    style={{ ...styles.input, ...styles.narrow }}
                    value={form.currency}
                    onChange={(e) => this.setField('currency', e.target.value)}
                  />
                </td>
                <td style={styles.td}>
                  <input
                    style={styles.input}
                    value={form.notes}
                    onChange={(e) => this.setField('notes', e.target.value)}
                  />
                </td>
                <td style={styles.td}>
                  <button type="button" style={styles.button} disabled={busy} onClick={() => this.save()}>
                    {form.id ? 'Save' : 'Add'}
                  </button>
                  {form.id && (
                    <button type="button" style={styles.secondary} onClick={this.cancel}>Cancel</button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {saved && <div style={styles.ok}>Saved. New estimates use this rate; figures already stored are unchanged.</div>}
      </div>
    );
  }
}

export default compose(
  graphql(repairRatesQuery, {
    name: 'rates',
    options: {
      // Retired rates are shown so they can be brought back.
      variables: { includeInactive: true },
      fetchPolicy: 'network-only',
    },
  }),
  graphql(saveRepairRateMutation, { name: 'saveRate' }),
)(RepairRatesPanel);
